/* eslint-disable max-lines */
/**
 * PhotoMapWall, a component that combines a map plus slideshow at the top of /hike pages.
 */

import React from 'react';
import getPhotoScaledUrl from '../../utils/gaia/getPhotoScaledUrl';
import {RegionType} from '../../utils/gaia/RegionType';
import {SPRING} from '../../colors';
import {startsWith} from 'lodash';
import withWidth from '@material-ui/core/withWidth';
import startReview from './startReview';
import getQueryParams from '../../utils/getQueryParams';
import mapboxgl from 'mapbox-gl';
import trackAnalytics from '../../utils/gaia/trackAnalytics';
import experiments from '../../utils/gaia/experiments';

const defaultMapPadding = 20;
const defaultStartPointMapPadding = 40;
const expandedMapHeight = 500;
const shrunkMapHeight = 300;
const nullMapMarker = {type: 'FeatureCollection', features: []};

class PhotoMapWall extends React.Component {
    constructor(props) {
        super(props);
        this.photoLightboxCom = props.photoLightboxCom;
        this.state = {
            photos: props.photos || [],
            cover_photo_id: props.cover_photo_id,
            title: props.title || null,
            isMapExpanded: props.isMapExpanded
        };
    }

    componentDidUpdate(prevProps, prevState) {
        if (!this.map) {
            return;
        }
        if (prevState.isMapExpanded !== this.state.isMapExpanded) {
            this.map.resize();
        }

        // Highlight from hovering on hike cards.
        if (this.props.highlightId) {
            this.highlightMap(prevProps);
        } else if (this.lastHighlightMarker) {
            this.lastHighlightMarker.remove();
        }

        // Highlight from hovering on elevation graph.
        if (this.map.getSource('marker')) {
            this.map.getSource('marker').setData(this.props.mapMarker || nullMapMarker);
        }
    }

    // Highlights/unhighlights a point on the map (route start point or object's centroid),
    // when hovering on cards in the UI elsewhere.
    // Also highlights the line on the map for known routes if zoomed in enough.
    highlightMap(prevProps) {
        // eslint-disable-next-line max-len
        if (JSON.stringify(prevProps.highlightPoint) !== JSON.stringify(this.props.highlightPoint)) {
            const el = document.createElement('div');
            el.className = 'marker marker-selected';
            if (this.props.highlightIndex >= 0) {
                el.innerHTML = '<span><b>' + (this.props.highlightIndex + 1) + '</b></span>';
            } else {
                el.innerHTML = '<span><b></b></span>';
            }

            // make a marker for the hovered card
            this.lastHighlightMarker = new mapboxgl.Marker(el)
                .setLngLat(this.props.highlightPoint.coordinates)
                .addTo(this.map);
        }
        if (this.props.highlightType === RegionType.KNOWN_ROUTE) {
            this.map.setFilter('knownRoutesHover', ['==', 'id', this.props.highlightId]);
        }
    }

    isMobileWidth() {
        return this.props.width === 'xs' || this.props.width === 'sm';
    }

    componentDidMount() {
        const base_url = window.location.origin;
        const mapSourcesApp = 'gaiagps-ng-2019';
        // eslint-disable-next-line max-len
        fetch(`${base_url}/mapsources.json?vector=1&deprecated=1&subscription=1&app=${mapSourcesApp}`)
            .then(response => {
                return response.json();
            }).then(mapSourcesList => {
                const map = new mapboxgl.Map({
                    container: 'photomapwall-map',
                    style: 'https://static.gaiagps.com/GaiaTopoGL/gaiatopo-feet.json',
                    interactive: !this.isMobileWidth(),
                    pitchWithRotate: false,
                    dragRotate: false,
                    transformRequest: (url, resourceType) => {
                        if (resourceType === 'Tile' && startsWith(url, 'g://')) {
                            // Get unique key
                            const key = url.substring(4).split('/')[0];
                            // Get {z}/{x}/{y} params
                            const url_params = url.split(key)[1].split('/');

                            for (let i = 0; i < mapSourcesList.mapSources.length; i++) {
                                if (mapSourcesList.mapSources[i].uniqueTilecacheKey === key) {
                                    return {
                                        url: mapSourcesList.mapSources[i].tileUrl
                                            .replace('{z}', url_params[1])
                                            .replace('{x}', url_params[2])
                                            .replace('{y}', url_params[3])
                                    };
                                }
                            }
                        }
                    }
                });
                this.map = map;
                // https://github.com/mapbox/mapbox-gl-js/issues/1715
                const mapFullyLoaded = () => {
                    if (map.loaded() && document.readyState === 'complete') {
                        this.scrollToNewReview();
                    } else {
                        setTimeout(() => {
                            mapFullyLoaded();
                        }, 100);
                    }
                };
                map.once('render', () => mapFullyLoaded());
                this.map.on('load', () => {
                    const tileUrl = window.location.protocol + window.location.host +
                        '/api/trip/tiles/{z}/{x}/{y}.mvt';

                    this.map.addSource('knownroutes', {
                        type: 'vector',
                        tiles: [tileUrl],
                        minzoom: 5,
                        maxzoom: 12
                    });

                    this.map.moveLayer('trail-case', 'road-lowzoom');
                    this.map.moveLayer('trail-nwn-case', 'road-lowzoom');

                    this.map.addLayer({
                        id: 'knownRoutes',
                        type: 'line',
                        'source-layer': 'knownroutes',
                        source: 'knownroutes',
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round'
                        },
                        paint: {
                            'line-color': '#FFD363',
                            'line-width': 4
                        }
                    }, 'road-lowzoom');

                    this.map.addLayer({
                        id: 'knownRoutesHover',
                        type: 'line',
                        'source-layer': 'knownroutes',
                        source: 'knownroutes',
                        filter: ['==', 'id', ''],
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round'
                        },
                        paint: {
                            'line-color': '#FF7F00',
                            'line-width': 4
                        }
                    }, 'road-lowzoom');

                    this.map.on('mousemove', 'knownRoutes', e => {
                        for (let i = 0; i < e.features.length; i++) {
                            if (e.features[i].properties.id.toString() === this.props.regionId) {
                                return;
                            }
                        }
                        const feature = e.features[0];
                        map.getCanvas().style.cursor = 'pointer';
                        map.setFilter('knownRoutesHover', ['==', 'id', feature.properties.id]);
                    });

                    this.map.on('mouseleave', 'knownRoutesHover', () => {
                        map.getCanvas().style.cursor = '';
                        map.setFilter('knownRoutesHover', ['==', 'id', '']);
                    });

                    this.map.on('click', 'knownRoutesHover', e => {
                        experiments.goal('click hike on hike page map');
                        for (let i = 0; i < e.features.length; i++) {
                            if (e.features[i].properties.id.toString() === this.props.regionId) {
                                return;
                            }
                        }
                        const feature = e.features[0];
                        const url = '/hike/' + feature.properties.id + '/';
                        trackAnalytics('Click Hike on Map', {category: 'Hike', url: url});
                        window.open(url);
                    });

                    if (this.props.onMapMove) {
                        this.map.on('move', this.props.onMapMove);
                    }

                    const props = this.props;
                    if (props.geometry) {
                        if (props.geometry.type === 'LineString') {
                            const coordinates = props.geometry.coordinates;
                            const lineBounds = coordinates.reduce((bounds, coord) => {
                                return bounds.extend(coord);
                            }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
                            this.map.fitBounds(lineBounds, {
                                padding: defaultMapPadding,
                                animate: false
                            });
                            this.map.addLayer({
                                id: 'mapObject',
                                type: 'line',
                                source: {
                                    type: 'geojson',
                                    data: {
                                        type: 'Feature',
                                        properties: {},
                                        geometry: props.geometry
                                    },
                                },
                                layout: {
                                    'line-join': 'round',
                                    'line-cap': 'round'
                                },
                                paint: {
                                    'line-color': '#F00',
                                    'line-width': 7
                                }
                            }, 'knownRoutesHover');
                        } else if (
                            props.geometry.type === 'Polygon' ||
                            props.geometry.type === 'MultiPolygon'
                        ) {
                            this.fitToExtent(props.extent, defaultMapPadding, false);
                        }
                    }
                    if (props.startPointsExtent) {
                        this.fitToExtent(
                            props.startPointsExtent,
                            defaultStartPointMapPadding,
                            false
                        );
                    } else if (props.extent) {
                        this.fitToExtent(props.extent, defaultMapPadding, false);
                    } else {
                        this.map.setCenter(props.centroid);
                        this.map.setZoom(15);
                    }

                    if (props.startPoints) {
                        props.startPoints.reverse().forEach((marker, i) => {
                            const el = document.createElement('div');
                            el.className = 'marker';
                            // eslint-disable-next-line max-len
                            el.innerHTML = '<span><b>' + (props.startPoints.length - i) + '</b></span>';
                            el.addEventListener('click', e => {
                                const url = '/hike/' + marker.id + '/';
                                trackAnalytics('Click Hike Marker', {
                                    category: 'Hike',
                                    url: url
                                });
                                window.open(url);
                                e.stopPropagation();
                            }, false);


                            // make a marker for each feature and add it to the map
                            new mapboxgl.Marker(el)
                                .setLngLat(marker.geometry.coordinates)
                                .addTo(this.map);
                        });
                    }

                    this.map.addLayer({
                        id: 'marker',
                        type: 'circle',
                        source: {
                            type: 'geojson',
                            data: props.mapMarker || nullMapMarker
                        },
                        layout: {},
                        paint: {
                            'circle-color': SPRING,
                            'circle-radius': 8
                        }
                    });

                    this.map.addControl(new mapboxgl.NavigationControl({showCompass: false}));
                });
            });
    }

    scrollToNewReview() {
        const url = window.location.href;
        if (url.search('rate_it') !== -1) {
            startReview({
                reviews: this.props.reviews,
                user: this.props.user,
                reviewText: getQueryParams(document.location.search).review_text,
                stars: parseInt(getQueryParams(document.location.search).rate_it),
                // regionType: "{{region_type}}",
                regionId: this.props.regionId,
                selectors: {reviewForm: '#reviewForm'},
            }
            );
            const reviewForm = document.getElementById('reviewForm');
            // I gave up trying to understand why this is the right value.
            const haxPadding = 528;
            const top = reviewForm.offsetTop + haxPadding;
            // https://stackoverflow.com/questions/17722497/
            const startingY = window.pageYOffset;
            const diff = top - startingY;
            const duration = 500;
            const easing = function (t) {
                return t < .5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
            };
            let start;
            window.requestAnimationFrame(function step(timestamp) {
                if (!start) start = timestamp;
                const time = timestamp - start;
                let percent = Math.min(time / duration, 1);
                percent = easing(percent);
                window.scrollTo(0, startingY + diff * percent);
                if (time < duration) {
                    window.requestAnimationFrame(step);
                }
            });
        }
    }

    fitToExtent(extent, padding, animated) {
        const bounds = new mapboxgl.LngLatBounds([extent[0], extent[1]], [extent[2], extent[3]]);
        this.map.fitBounds(bounds, {
            padding: padding,
            animate: animated
        });
    }

    handlePhotoClick(e) {
        if (!this.photoLightboxCom) {
            return;
        }
        e.preventDefault();
        const photoIndex = parseInt(e.currentTarget.getAttribute('data-photo-index'), 10);
        trackAnalytics('Photo Click', {category: 'Hike', index: photoIndex});
        this.photoLightboxCom.setState({
            photos: this.state.photos,
            isVisible: true,
            index: photoIndex,
            title: this.state.title,
        });
    }

    toggleMapSize() {
        this.setState({isMapExpanded: !this.state.isMapExpanded});
        if (this.props.switchContainersMethod) {
            this.props.switchContainersMethod();
        }
        trackAnalytics('Change Map Size', {
            category: 'Hike',
            expanded: this.state.isMapExpanded
        });
    }

    render() {
        let photoMapWallPhotoMain = '';
        let photoMapWallPhotoSecondary = '';
        let mainPhotoEl = '';
        let secondaryPhoto1El = '';
        let secondaryPhoto2El = '';

        const showSlideshow = (
            this.props.regionType === RegionType.KNOWN_ROUTE &&
            !this.state.isMapExpanded
        );

        if (showSlideshow && this.state.photos.length > 0) {

            const mainPhoto = getPhotoScaledUrl(this.state.photos[0].id);
            const mainPhotoStyle = {backgroundImage: 'url(' + mainPhoto + ')'};
            mainPhotoEl = (<div
                onClick={e => this.handlePhotoClick(e)}
                data-photo-index={0}
                style={mainPhotoStyle}
                className='photomapwall-photo photomapwall-photomain-photo'
            >
                <span className='ga-button ga-button-outline-white'>
                    View Photos ({this.props.photoCount})
                </span>
            </div>);

            photoMapWallPhotoMain = (<div className='photomapwall-photomain'>
                { mainPhotoEl }
            </div>);
        }

        if (showSlideshow && this.state.photos.length > 1) {
            const secondaryPhoto1 = getPhotoScaledUrl(this.state.photos[1].id);
            const secondaryPhoto1Style = {backgroundImage: 'url(' + secondaryPhoto1 + ')'};
            secondaryPhoto1El = <div
                onClick={e => this.handlePhotoClick(e)}
                data-photo-index={1}
                style={secondaryPhoto1Style}
                className='photomapwall-photo photomapwall-photosecondary-photo'
            />;
        }

        if (showSlideshow && this.state.photos.length > 2) {
            const secondaryPhoto2 = getPhotoScaledUrl(this.state.photos[2].id);
            const secondaryPhoto2Style = {backgroundImage: 'url(' + secondaryPhoto2 + ')'};
            secondaryPhoto2El = <div
                onClick={e => this.handlePhotoClick(e)}
                data-photo-index={2}
                style={secondaryPhoto2Style}
                className='photomapwall-photo photomapwall-photosecondary-photo'
            />;
        }

        if (showSlideshow && this.state.photos.length > 1) {
            photoMapWallPhotoSecondary = !this.isMobileWidth() && (
                <div className='photomapwall-photosecondary'>
                    { secondaryPhoto1El }
                    { secondaryPhoto2El }
                </div>
            );
        }

        let buttonName = 'fullscreen';
        if (this.state.isMapExpanded) {
            buttonName = 'fullscreen_exit';
        }
        const fullscreenButton = !this.isMobileWidth() && (
            <div className="photowall-map-expand" onClick={() => this.toggleMapSize()}>
                <i className="material-icons">{buttonName}</i>
            </div>
        );
        return (
            <div className='photomapwall'>
                { photoMapWallPhotoMain }
                { photoMapWallPhotoSecondary }
                { fullscreenButton }
                <div
                    className='photomapwall-map'
                    id='photomapwall-map'
                    style={{height: this.state.isMapExpanded ? expandedMapHeight : shrunkMapHeight}}
                />

            </div>
        );
    }
}

export default withWidth()(PhotoMapWall);
